import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  FINISH_IDS,
  FINISH_LOOK,
  FINISH_SHINE_MIN_PX,
  FINISH_SHINE_STAGGER,
  type FinishChoice,
  type FinishId,
  finishProgress,
} from '@goaltree/backend/convex/lib/dotFinishes'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { habitIconComponent } from '@/components/habit-icon'
import { useTheme } from '@/components/theme-provider'

type T = (k: string, o?: Record<string, unknown>) => string

/** The light sweep inside a finished cell; `i` staggers it along the row. */
export function FinishGlint({
  finish,
  i = 0,
  fast,
}: {
  finish: FinishId
  i?: number
  fast?: boolean
}) {
  return (
    <span
      aria-hidden
      className="dot-glint"
      data-sweep={fast ? 'fast' : FINISH_LOOK[finish].sweep}
      style={{ '--d': `${(i * FINISH_SHINE_STAGGER).toFixed(2)}s` } as CSSProperties}
    />
  )
}

/** Inset highlight (and sweep, when `i` is given and the cell is big enough). */
export function FinishSheen({ finish, size, i }: { finish: FinishId; size: number; i?: number }) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ boxShadow: FINISH_LOOK[finish].shadow.split(', 0 0')[0] }}
      />
      {i !== undefined && size >= FINISH_SHINE_MIN_PX && <FinishGlint finish={finish} i={i} />}
    </>
  )
}

export const finishFill = (finish: FinishId): CSSProperties => ({
  position: 'relative',
  backgroundColor: 'transparent',
  backgroundImage: FINISH_LOOK[finish].gradient,
})

export function useFinishText() {
  const { resolvedTheme } = useTheme()
  return (f: FinishId) => (resolvedTheme !== 'light' ? FINISH_LOOK[f].tone : FINISH_LOOK[f].text)
}

/** A finish's name, or the plain look. */
export const finishName = (t: T, f: FinishChoice) => t(`dotFinish.names.${f}`)

type Picker = NonNullable<FunctionReturnType<typeof api.dotFinishes.picker>>

function progressNote(t: T, f: FinishId, p: Picker) {
  const pr = finishProgress(f, { total: p.total, best: p.best })
  if (p.unlocked.includes(f))
    return pr.kind === 'level'
      ? t('dotFinish.earnedLevel', { level: pr.level })
      : t('dotFinish.earnedStreak', { n: pr.need })
  return pr.kind === 'level'
    ? t('dotFinish.toGo', { level: pr.level, n: pr.toGo })
    : t(p.cadence === 'day' ? 'dotFinish.streakDays' : 'dotFinish.streakRuns', {
        have: pr.have,
        need: pr.need,
      })
}

export function patchFinish(
  store: OptimisticLocalStore,
  habitId: Id<'nodes'>,
  finish: FinishChoice,
) {
  const value = finish === 'none' ? null : finish
  const habits = store.getQuery(api.nodes.getHabits, {})
  if (habits)
    store.setQuery(
      api.nodes.getHabits,
      {},
      habits.map((h) => (h._id === habitId ? { ...h, finish: value } : h)),
    )
  const p = store.getQuery(api.dotFinishes.picker, { habitId })
  if (p) store.setQuery(api.dotFinishes.picker, { habitId }, { ...p, selected: value })
  const s = store.getQuery(api.habitStats.getHabitStats, { habitId })
  if (s)
    store.setQuery(
      api.habitStats.getHabitStats,
      { habitId },
      { ...s, habit: { ...s.habit, finish: value } },
    )
}

export function useSetFinish() {
  return useMutation(api.dotFinishes.setDotFinish).withOptimisticUpdate((store, a) =>
    patchFinish(store, a.habitId, a.finish),
  )
}

/** The picker's Finish row: None plus every finish, locked ones with progress. */
export function FinishRow({
  habitId,
  value,
  onChange,
}: {
  habitId: Id<'nodes'>
  value: FinishId | null
  onChange: (f: FinishId | null) => void
}) {
  const picker = useQuery(api.dotFinishes.picker, { habitId })
  const save = useSetFinish()
  return (
    <FinishRowView
      picker={picker ?? undefined}
      value={value}
      onPick={(f) => {
        onChange(f === 'none' ? null : f)
        void save({ habitId, finish: f }).catch(() => {})
      }}
    />
  )
}

export function FinishRowView({
  picker,
  value,
  onPick: pick,
}: {
  picker: Picker | undefined
  value: FinishId | null
  onPick: (f: FinishChoice) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
        {t('dotFinish.section')}
      </span>
      <div className="grid grid-cols-3 gap-2">
        {(['none', ...FINISH_IDS] as const).map((f) => {
          const locked = f !== 'none' && !!picker && !picker.unlocked.includes(f)
          const selected = (value ?? 'none') === f
          return (
            <button
              key={f}
              type="button"
              disabled={!picker || locked}
              aria-pressed={selected}
              onClick={() => pick(f)}
              className={cn(
                'flex cursor-pointer flex-col items-center gap-1.5 rounded-2xl border bg-muted/40 px-1.5 py-2.5 transition-colors enabled:hover:bg-muted disabled:cursor-not-allowed',
                selected ? 'border-2 border-primary' : 'border-border',
                locked && 'opacity-55',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'relative block size-[30px] overflow-hidden rounded-[9px]',
                  locked && 'border-[1.5px] border-dashed border-foreground/25 bg-muted',
                  f === 'none' && 'bg-[var(--habit)]',
                )}
                style={f !== 'none' && !locked ? finishFill(f) : undefined}
              >
                {f !== 'none' && !locked && <FinishSheen finish={f} size={30} i={0} />}
              </span>
              <span className="text-xs font-bold">{finishName(t, f)}</span>
              <span className="text-center font-mono text-[10px] leading-tight text-muted-foreground">
                {f === 'none' ? t('dotFinish.plain') : picker ? progressNote(t, f, picker) : ' '}
              </span>
            </button>
          )
        })}
      </div>
      <p className="text-xs text-muted-foreground">{t('dotFinish.note')}</p>
    </div>
  )
}

// ─── Unlock card ─────────────────────────────────────────────────────────────

type Card = NonNullable<FunctionReturnType<typeof api.dotFinishes.unseen>>

export function FinishUnlockCard({
  card,
  onUse,
  onKeep,
}: {
  card: Card
  onUse: () => void
  onKeep: () => void
}) {
  const { t } = useTranslation()
  const ink = useFinishText()
  const f = card.finish
  const look = FINISH_LOOK[f]
  const Icon = habitIconComponent(card.icon)
  const habit = card.title.split('///')[0].trim()
  const keep = card.current
  const next = card.next
  const pr = next ? finishProgress(next, { total: card.total, best: card.best }) : null
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span
        className="relative grid size-[88px] place-items-center overflow-hidden rounded-[24px]"
        style={{
          backgroundImage: look.gradient,
          boxShadow: `inset 0 2px 0 rgba(255,255,255,0.55), 0 0 40px ${look.tone}73`,
          color: look.ink,
        }}
      >
        <Icon className="relative size-10" strokeWidth={2.2} />
        <FinishGlint finish={f} fast />
      </span>
      <div className="flex flex-col gap-1.5">
        <span
          className="font-mono text-[11px] tracking-[0.14em] uppercase"
          style={{ color: ink(f) }}
        >
          {f === 'diamond'
            ? t('dotFinish.unlock.eyebrowStreak', { n: card.best, habit })
            : t('dotFinish.unlock.eyebrowLevel', { level: card.level, habit })}
        </span>
        <DialogTitle className="text-[26px] leading-tight font-black tracking-tight">
          {t('dotFinish.unlock.title', { name: finishName(t, f) })}
        </DialogTitle>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {f === 'diamond'
            ? t('dotFinish.unlock.bodyStreak', { habit })
            : t('dotFinish.unlock.bodyLevel', {
                n: card.level,
                habit,
                name: finishName(t, f),
              })}
        </p>
      </div>
      <div className="flex gap-[5px]">
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            aria-hidden
            className="relative block size-[22px] overflow-hidden rounded-[6px]"
            style={finishFill(f)}
          >
            <FinishGlint finish={f} i={i * 0.9} fast />
          </span>
        ))}
      </div>
      <div className="flex w-full flex-col gap-2">
        <button
          type="button"
          onClick={onUse}
          className="cursor-pointer rounded-[14px] p-3.5 text-[15px] font-extrabold transition-opacity hover:opacity-90"
          style={{ backgroundImage: look.flat, color: look.ink }}
        >
          {t('dotFinish.unlock.use', { name: finishName(t, f) })}
        </button>
        <button
          type="button"
          onClick={onKeep}
          className="cursor-pointer rounded-[14px] border border-border p-3 text-sm font-semibold text-foreground/85 transition-colors hover:bg-muted"
        >
          {keep === 'none'
            ? t('dotFinish.unlock.keepPlain')
            : t('dotFinish.unlock.keep', { name: finishName(t, keep) })}
        </button>
        <span className="text-xs text-muted-foreground">{t('dotFinish.unlock.switchAnytime')}</span>
      </div>
      <span className="text-xs text-muted-foreground">
        {!next || !pr
          ? t('dotFinish.unlock.allEarned')
          : pr.kind === 'level'
            ? t('dotFinish.unlock.nextLevel', {
                name: finishName(t, next),
                level: pr.level,
                count: pr.toGo,
              })
            : t('dotFinish.unlock.nextStreak', {
                name: finishName(t, next),
                have: pr.have,
                need: pr.need,
              })}
      </span>
    </div>
  )
}

/** Pops each habit's unseen finish unlock once, after any badge popup. */
export function FinishCelebration() {
  const card = useQuery(api.dotFinishes.unseen)
  const badges = useQuery(api.progression.getUnseenBadges)
  const markSeen = useMutation(api.dotFinishes.markSeen).withOptimisticUpdate((store) =>
    store.setQuery(api.dotFinishes.unseen, {}, null),
  )
  const save = useSetFinish()
  const [shown, setShown] = useState<Card | null>(null)
  const dismissed = useRef(new Set<string>())
  useEffect(() => {
    if (shown || !card || badges === undefined || badges.length) return
    if (!dismissed.current.has(`${card.habitId}:${card.finish}`)) setShown(card)
  }, [card, badges, shown])

  const close = (finish?: FinishChoice) => {
    if (!shown) return
    dismissed.current.add(`${shown.habitId}:${shown.finish}`)
    if (finish && finish !== shown.current)
      void save({ habitId: shown.habitId, finish }).catch(() => {})
    void markSeen({ habitId: shown.habitId }).catch(() => {})
    setShown(null)
  }
  return (
    <Dialog open={!!shown} onOpenChange={(o) => !o && close()}>
      {shown && (
        <DialogContent
          showCloseButton={false}
          className="max-w-[350px] rounded-[26px] px-[22px] pt-7 pb-[22px]"
        >
          <FinishUnlockCard card={shown} onUse={() => close(shown.finish)} onKeep={() => close()} />
        </DialogContent>
      )}
    </Dialog>
  )
}
